import { Injectable } from '@angular/core';

/**
 * Last line of defence against redirect loops between Shell and Legacy. Every round trip passes the landing
 * route: Legacy sends a user it cannot serve back to the Shell (sign-in with deeplink, `/landing`), and the
 * landing route sends him to Legacy again. A user follows such a hand-over only a few times a minute; when the
 * hand-overs come faster, the applications disagree about the session and the user is stopped on the error page.
 */
@Injectable({ providedIn: 'root' })
export class HandoffLoopBreaker {
  private static readonly storageKey = 'portal.legacyHandoffs';
  private static readonly windowMs = 60_000;
  private static readonly maxHandoffs = 4;

  /** Records a hand-over to a page outside the Shell; `false` when the budget of the current window is used up. */
  allowHandoff(): boolean {
    const now = Date.now();
    const recent = this.read().filter(at => now - at < HandoffLoopBreaker.windowMs);
    if (recent.length >= HandoffLoopBreaker.maxHandoffs) {
      // Start over, so the user can try again once the cause is resolved
      sessionStorage.removeItem(HandoffLoopBreaker.storageKey);
      return false;
    }
    sessionStorage.setItem(HandoffLoopBreaker.storageKey, JSON.stringify([...recent, now]));
    return true;
  }

  private read(): number[] {
    try {
      const value = JSON.parse(sessionStorage.getItem(HandoffLoopBreaker.storageKey) ?? '[]');
      return Array.isArray(value) ? value.filter((at): at is number => typeof at === 'number') : [];
    } catch {
      return [];
    }
  }
}
